import { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Email, Inbox } from '../types/email';
import { mergeWithDefaultEmails } from '../utils/defaultEmails';

// Define valid email domains
const VALID_EMAIL_DOMAINS = ['mailderp.com'];

// Word lists for random email generation
const ADJECTIVES = [
  'happy', 'clever', 'brave', 'calm', 'eager', 'fierce', 'gentle', 'honest', 
  'jolly', 'kind', 'lively', 'noble', 'polite', 'quick', 'smart', 'tidy', 'wise',
  'zany', 'quirky', 'silly', 'spunky', 'witty', 'zesty', 'bubbly', 'charming',
  'daring', 'elegant', 'fancy', 'graceful', 'jumpy', 'kooky', 'lucky', 'merry',
];

const NOUNS = [
  'tiger', 'panda', 'eagle', 'wolf', 'bear', 'fox', 'rabbit', 'deer', 'owl',
  'falcon', 'dolphin', 'koala', 'penguin', 'turtle', 'zebra', 'elephant',
  'giraffe', 'kangaroo', 'hippo', 'rhino', 'sloth', 'otter', 'seal', 'whale',
  'coyote', 'lynx', 'badger', 'raccoon', 'skunk', 'porcupine', 'armadillo',
];

export interface EmailContextType {
  currentInbox: Inbox | null;
  inboxes: Inbox[];
  emails: Email[];
  selectedEmail: Email | null;
  loading: boolean;
  error: string | null;
  fetchEmails: (inboxId?: number) => Promise<void>;
  switchInbox: (inbox: Inbox) => void;
  createNewMailbox: () => Promise<Inbox>;
  markEmailAsRead: (emailId: number) => void;
  selectEmail: (email: Email | null) => void;
  setEmails: React.Dispatch<React.SetStateAction<Email[]>>;
  validDomains: string[];
  generateRandomEmail: () => string;
}

export interface EmailProviderProps {
  children: ReactNode;
}

// Helper function to generate random email addresses
const generateRandomEmail = (): string => {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const randomNum = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
  return `${adjective}${noun}${randomNum}@${VALID_EMAIL_DOMAINS[0]}`;
};

// Create the email context
const EmailContext = createContext<EmailContextType | null>(null);

// Define the provider component
export function EmailProvider({ children }: EmailProviderProps) {
  const [currentInbox, setCurrentInbox] = useState<Inbox | null>(null);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Function to create a new mailbox - removed name parameter
  const createNewMailbox = useCallback(async (): Promise<Inbox> => {
    setLoading(true);
    setError(null);
    
    try {
      const email = generateRandomEmail();
      
      // This will be replaced with actual API call later
      const response = await new Promise<{ data: Inbox }>(resolve => 
        setTimeout(() => resolve({ 
          data: {
            id: Date.now(), // Using timestamp as unique ID for now
            email
          }
        }), 500)
      );
      
      const newInbox = response.data;
      setInboxes(prevInboxes => [...prevInboxes, newInbox]);
      
      // Always set the new inbox as current
      setCurrentInbox(newInbox);
      
      return newInbox;
    } catch (err) {
      setError('Failed to create new mailbox');
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to fetch emails for the current inbox
  const fetchEmails = useCallback(async (inboxId?: number) => {
    if (!inboxId && !currentInbox) return;

    setLoading(true);
    setError(null);
    
    try {
      // Get the email address for the inbox
      const emailAddress = currentInbox?.email || '';
      
      // Call our API endpoint
      const response = await fetch(`/api/email/${encodeURIComponent(emailAddress)}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching emails: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Add the welcome email to the results
      setEmails(mergeWithDefaultEmails(data, emailAddress));
      
      // Clear selected email when switching inboxes
      setSelectedEmail(null);
    } catch (err) {
      setError('Failed to fetch emails');
      console.error(err);
      
      // Fallback to mock data in development
      if (import.meta.env.DEV) {
        console.log('Using mock data in development');
        const mockEmails = [{ 
          id: 1, 
          subject: 'Welcome to MailDerp', 
          from: 'team@mailderp.com',
          to: currentInbox?.email || '',
          body: 'Welcome to MailDerp! We\'re excited to have you on board.',
          htmlBody: '<h2>Welcome to MailDerp!</h2><p>We\'re excited to have you on board.</p>',
          read: false, 
          date: new Date(Date.now() - 3600000) // 1 hour ago
        }];
        
        setEmails(mergeWithDefaultEmails(mockEmails, currentInbox?.email || ''));
      }
    } finally {
      setLoading(false);
    }
  }, [currentInbox]);

  // Function to switch the current inbox - memoized with useCallback
  const switchInbox = useCallback((inbox: Inbox) => {
    setCurrentInbox(inbox);
    fetchEmails(inbox.id);
  }, [fetchEmails]);

  // Function to mark an email as read
  const markEmailAsRead = useCallback((emailId: number) => {
    setEmails(prevEmails => 
      prevEmails.map(email => 
        email.id === emailId ? { ...email, read: true } : email
      )
    );
  }, []);

  // Function to select an email for viewing
  const selectEmail = useCallback((email: Email | null) => {
    setSelectedEmail(email);
    if (email && !email.read) {
      markEmailAsRead(email.id);
    }
  }, [markEmailAsRead]);

  // Initialize with a random mailbox on first load
  useEffect(() => {
    if (inboxes.length === 0) {
      createNewMailbox();
    }
  }, [inboxes.length, createNewMailbox]);

  const value: EmailContextType = {
    currentInbox,
    inboxes,
    emails,
    selectedEmail,
    loading,
    error,
    fetchEmails,
    switchInbox,
    createNewMailbox,
    markEmailAsRead,
    selectEmail,
    setEmails,
    validDomains: VALID_EMAIL_DOMAINS,
    generateRandomEmail,
  };

  return <EmailContext.Provider value={value}>{children}</EmailContext.Provider>;
}

export default EmailContext;